import { observable, action } from "mobx"
import api from "../services/ApiDefault"
import { showResponseError, showMessage } from "../utilities/utils"
import AppConfig from "../config/AppConfig"

export class InvoiceStore {
  @observable isLoading = false
  @observable invoices = []
  @observable params = { page: 1, perPage: 20, total: 0, lastPage: 1 }
  @observable earnings = null

  // Staff: get my invoices
  @action
  async getMyInvoices(params = {}) {
    this.isLoading = true
    const response = await api.getInvoices(params)
    if (response.ok) {
      const { data, page, perPage, total, lastPage } = response.data.data
      this.invoices = data
      this.params = { page, perPage, total, lastPage }
    } else {
      showResponseError(response)
    }
    this.isLoading = false
  }

  // Staff: get earnings summary
  @action
  async getEarnings(billingPeriod) {
    const params = {}
    if (billingPeriod) params.billing_period = billingPeriod
    const response = await api.getInvoiceEarnings(params)
    if (response.ok) {
      this.earnings = response.data.data
    } else {
      showResponseError(response)
    }
  }

  // Staff: generate invoice
  @action
  async generateInvoice(billingPeriod) {
    this.isLoading = true
    const response = await api.generateInvoice({ billing_period: billingPeriod })
    this.isLoading = false
    if (response.ok) {
      showMessage("Invoice generated successfully")
      this.getMyInvoices({ page: 1 })
      return response.data.data
    } else {
      showResponseError(response)
      return null
    }
  }

  // Staff: resend invoice email
  @action
  async resendInvoice(id) {
    const response = await api.resendInvoice(id)
    if (response.ok) {
      showMessage("Invoice resent successfully")
      this.updateInvoiceInList(id, response.data.data)
    } else {
      showResponseError(response)
    }
  }

  // Send pending invoice
  @action
  async sendInvoice(id) {
    const response = await api.sendInvoice(id)
    if (response.ok) {
      showMessage("Invoice sent successfully")
      this.updateInvoiceInList(id, response.data.data)
    } else {
      showResponseError(response)
    }
  }

  // Admin: get all invoices
  @action
  async getAdminInvoices(params = {}) {
    this.isLoading = true
    const response = await api.getInvoicesAdmin(params)
    if (response.ok) {
      const { data, page, perPage, total, lastPage } = response.data.data
      this.invoices = data
      this.params = { page, perPage, total, lastPage }
    } else {
      showResponseError(response)
    }
    this.isLoading = false
  }

  // Admin: mark as paid
  @action
  async markPaid(id) {
    const response = await api.markInvoicePaid(id)
    if (response.ok) {
      showMessage("Invoice marked as paid")
      this.updateInvoiceInList(id, response.data.data)
    } else {
      showResponseError(response)
    }
  }

  // Admin: reject invoice
  @action
  async rejectInvoice(id, reason) {
    const response = await api.rejectInvoice(id, { reason })
    if (response.ok) {
      showMessage("Invoice rejected")
      this.updateInvoiceInList(id, response.data.data)
    } else {
      showResponseError(response)
    }
  }

  // Admin: request more info
  @action
  async requestInfo(id, message) {
    const response = await api.requestInvoiceInfo(id, { message })
    if (response.ok) {
      showMessage("Info requested from host")
      this.updateInvoiceInList(id, response.data.data)
    } else {
      showResponseError(response)
    }
  }

  // Shared
  downloadInvoice(id) {
    window.open(`${AppConfig.apiURL}/invoices/${id}/download`, "_blank")
  }

  @action
  updateInvoiceInList(id, data) {
    const index = this.invoices.findIndex((i) => i._id === id)
    if (index !== -1) {
      this.invoices[index] = { ...this.invoices[index], ...data }
    }
  }
}

const invoiceStore = new InvoiceStore()
export default invoiceStore
